'use client';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import CreditCardIcons from '@/components/customercenter-ui/svgs/creditCards';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import ToolTips from '@/components/customercenter-ui/tooltip';
import SecurityCodeTooltip from '@/components/customercenter-ui/tooltip/securityCode';
import { useAccount } from '@/frontastic';
import { SuccessNotification } from '@/helpers/utils/notification';
import {
  combinedKeyPressHandler,
  formatCurrency,
  getCurrentDateTime,
  getAgentName,
  getCookieValue,
} from '@/helpers/utils/siteUtils';
import { CustomerAddressPayload, TokenizeCardPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { COOKIE_KEYS, MAX_PAGE_SIZE } from '../../../../../constants/constant';
import {
  CARD_NAMES,
  PAYLOAD_QUERY,
  PAYMENT_FORM_SPECS,
  RESPONSE_SUCCESS_MESSAGE,
} from '../../customer-detail/constants';
import { AdjsutPaymentSchema } from '../../customer-detail/schema';
import { ShippingAddressDetailsType } from '../../customer-detail/types';
import { getCardType, handleCardNumberChange, transformShippingAddress } from '../../customer-detail/utils';
import { billingAddress } from '../../order-details/types';
import { Button } from '../../ui/button';
import InputField from '../../ui/customInput/input';
import { CustomFloatingSelect } from '../../ui/customSelect/floatingSelect';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import Loader from '../../ui/loader';
import { Textarea } from '../../ui/textarea';
import { useToast } from '../../ui/use-toast';
import { maskPhoneNumber, maskPostalCode } from '../../utils/formatUtils';
import EditAddressModal from '../customerDetailAction/editAddress';

const AdjustPaymentMethodModal = ({
  customerId,
  open,
  setOpen,
  primaryEmail,
  billingAddress,
  Texts,
  startRecording,
  getUpdatedData,
  orderBalance,
  addNewNotes,
}: {
  customerId?: string;
  open?: boolean;
  setOpen?: any;
  primaryEmail?: string;
  billingAddress: billingAddress;
  buttonCss?: string;
  Texts?: any;
  startRecording: () => void;
  getUpdatedData: () => void;
  orderBalance: number | undefined;
  addNewNotes: any;
}) => {
  const { toast } = useToast();
  const params = useSearchParams();
  const [loading, setLoading] = useState(false);
  const { getCustomerAddresses, tokenizeAndUpdateOrder, addOrderNote } = useAccount();
  const [addresses, setAddresses] = useState<ShippingAddressDetailsType[]>([]);
  const [cardType, setCardType] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expDate, setExpDate] = useState('');
  const [selectedBillingAddress, setSelectedBillingAddress] = useState<ShippingAddressDetailsType | any>({});
  const [openModal, setOpenModal] = useState(false);
  const [agentName] = useState(getAgentName().replace(/@.*/, ''));

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
  } = useForm({
    resolver: zodResolver(AdjsutPaymentSchema),
    mode: 'all',
  });

  const onSubmit = async (data: any) => {
    setLoading(true);
    const CardInfo = {
      nameOnCard: selectedBillingAddress?.fullName,
      cardIssuerName: cardType,
      expirationMonth: data?.expDate?.split('/')[0],
      expirationYear: `20${data?.expDate?.split('/')[1]}`,
    };
    const payload = {
      query: {
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE) ?? '',
      },
      data: {
        orderId: params.get(PAYLOAD_QUERY.ORDER_ID) ?? '',
        CustomerInfo: {
          customerId: customerId,
          webSessionId: '',
          ipAddress: '',
          email: primaryEmail ?? '',
          phone: selectedBillingAddress?.phone,
        },
        BillingAddress: {
          line1: selectedBillingAddress?.streetName,
          line2: selectedBillingAddress?.additionalAddressInfo,
          city: selectedBillingAddress?.city,
          state: selectedBillingAddress?.state,
          zip: selectedBillingAddress?.postalCode,
          company: selectedBillingAddress?.company,
          country: selectedBillingAddress?.country,
        },
        CardInfo: {
          nameOnCard: CardInfo?.nameOnCard,
          cardIssuerName: cardType,
          pan: data?.cardNumber?.split(' ').join(''),
          expirationMonth: CardInfo?.expirationMonth,
          expirationYear: CardInfo?.expirationYear,
          securityCode: data?.cvv,
        },
      },
    } as TokenizeCardPayload;

    await tokenizeAndUpdateOrder(payload).then(async (response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        const currentDate = getCurrentDateTime();
        const payload = {
          data: {
            Note: {
              createdBy: agentName,
              createdDate: currentDate,
              isPriority: true,
              text: data?.note,
            },
          },
          query: {
            orderId: params.get(PAYLOAD_QUERY.ORDER_ID),
            divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
          },
        };

        await addOrderNote(payload).then(() => {
          if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
            addNewNotes();
          }
        });
        startRecording();
        setOpen(false);
        getUpdatedData();
        SuccessNotification(toast, {
          description: 'Payment method adjusted successfully',
          action: <SuccessIcon className="h-5 w-5" />,
        });
      } else {
      }
    });
    setLoading(false);
  };

  const handleCardNumberInputChange = (e: any) => {
    handleCardNumberChange(e, setCardNumber);
    setCardType(getCardType(e.target.value));
  };
  const handleExpDateInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    let formattedValue = value.replace(/\D/g, '');
    if (formattedValue.length === 1 && parseInt(formattedValue) > 1) {
      formattedValue = '0' + formattedValue;
    } else if (formattedValue.length === 2 && parseInt(formattedValue) > 12) {
      formattedValue = '12';
    }
    setExpDate(formattedValue.slice(0, 2) + (formattedValue.length > 2 ? '/' + formattedValue.slice(2) : ''));
  };

  const handleChange = (id: string) => {
    const selectedBillingAddress = addresses?.filter((address: any) => address?.addressId === id);
    if (selectedBillingAddress?.length > 0) {
      setSelectedBillingAddress(selectedBillingAddress[0]);
    } else {
      setSelectedBillingAddress(billingAddress);
    }
  };

  const getBillingAddresses = async () => {
    setLoading(true);
    const payload = {
      query: {
        omsCustomerId: customerId,
        pageSize: MAX_PAGE_SIZE,
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
    } as CustomerAddressPayload;
    await getCustomerAddresses(payload).then((response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        const transformedAddress = transformShippingAddress(response?.RecipientAddresses || []);
        setAddresses([billingAddress, ...transformedAddress]);
      }
    });
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      reset();
      setCardNumber('');
      setSelectedBillingAddress(billingAddress);
      setExpDate('');
      getBillingAddresses();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (!value) {
          startRecording();
        }
      }}
    >
      <DialogContent className="custom-scrollbar max-h-[90%] max-w-[640px] overflow-y-auto bg-white sm:rounded-md">
        {loading && <Loader />}
        <DialogHeader>
          {/* chng-text-25px */}
          <DialogTitle className="text-2xl font-bold">
            <section>
              <div className="mt-4 text-2xl font-bold">{Texts.adjustPaymentMethod}</div>
              <div className="text-xs font-normal">*{Texts.requiredFields}</div>
            </section>
          </DialogTitle>
        </DialogHeader>
        <div className="font-medium">
          {Texts.orderBalance}: <span className="font-bold">{formatCurrency(orderBalance?.toString() ?? '')}</span>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <section className="mb-4">
            <section className="flex w-full gap-4 px-1 pt-5">
              <section className="w-full max-w-[440px]">
                <InputField
                  items={PAYMENT_FORM_SPECS.cardNumber.placeHolder}
                  id={PAYMENT_FORM_SPECS.cardNumber.id}
                  htmlFor={PAYMENT_FORM_SPECS.cardNumber.id}
                  register={register}
                  errors={errors}
                  label={PAYMENT_FORM_SPECS.cardNumber.label}
                  classname="w-full"
                  value={cardNumber}
                  onChange={handleCardNumberInputChange}
                  cardType={cardType}
                  maxLength={cardType.toLowerCase() !== CARD_NAMES.AMERICAN_EXPRESS.toLowerCase() ? 19 : 18}
                />
              </section>
              <div className="gbomit pt-3">
                <CreditCardIcons />
              </div>
            </section>

            <section className="flex items-baseline gap-x-5 pl-1 pt-6">
              <section className="w-full max-w-[221px]">
                <InputField
                  items={PAYMENT_FORM_SPECS.expDate.placeHolder}
                  id={PAYMENT_FORM_SPECS.expDate.id}
                  htmlFor={PAYMENT_FORM_SPECS.expDate.id}
                  register={register}
                  errors={errors}
                  label={PAYMENT_FORM_SPECS.expDate.label}
                  classname="w-full"
                  value={expDate}
                  onChange={handleExpDateInputChange}
                  maxLength={5}
                />
              </section>
              <section className="w-full max-w-[173px]">
                <InputField
                  maxLength={cardType.toLowerCase() === CARD_NAMES.AMERICAN_EXPRESS ? 4 : 3}
                  items={PAYMENT_FORM_SPECS.cvv.placeHolder}
                  id={PAYMENT_FORM_SPECS.cvv.id}
                  htmlFor={PAYMENT_FORM_SPECS.cvv.id}
                  register={register}
                  errors={errors}
                  monetaryInput
                  label={PAYMENT_FORM_SPECS.cvv.label}
                  classname="w-full"
                />
              </section>

              <ToolTips Content={{ ContentToolTip: <SecurityCodeTooltip /> }} />
            </section>
          </section>
          <section>
            <h1 className="mb-4 text-lg font-bold">{Texts.billingAddress}</h1>
            <CustomFloatingSelect
              label={'billingAddress'}
              onChange={handleChange}
              id={'billingAddress'}
              register={register}
              setValue={setValue}
              errors={errors}
              classname={'w-[400px] ml-1'}
              placeholder={'*Select Address'}
              initialState={billingAddress?.addressId}
              options={addresses?.map((items: any) => (
                <option key={items?.addressId} value={items?.addressId} className="gbmask px-2">
                  <p className="gbmask"> {items?.fullName}</p>
                </option>
              ))}
            />
            <div className="gbmask ml-1 mt-2">
              {selectedBillingAddress !== undefined && (
                <>
                  <p>{selectedBillingAddress?.fullName}</p>
                  <p>
                    {selectedBillingAddress?.streetName} {selectedBillingAddress?.additionalAddressInfo}
                  </p>
                  <p>
                    {selectedBillingAddress?.city} ,{selectedBillingAddress?.state}{' '}
                    {maskPostalCode(selectedBillingAddress?.postalCode ?? '')}
                  </p>
                  <p className="my-2">{maskPhoneNumber(selectedBillingAddress?.phone ?? '')}</p>
                </>
              )}
              <button
                className="text-blueLight underline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpenModal(true);
                }}
                type="button"
              >
                {Texts.editAddress}
              </button>
              {openModal && (
                <EditAddressModal
                  Texts={Texts}
                  data={selectedBillingAddress}
                  setSelectedBillingAddress={setSelectedBillingAddress}
                  open={openModal}
                  setOpen={setOpenModal}
                  makeAPIcall={false}
                />
              )}
            </div>
          </section>
          <section>
            <section className="mt-4 h-[81px]">
              <Textarea
                placeholder="*Note"
                className={`${errors['note'] ? 'border-scarletRed text-scarletRed' : ''} h-full w-full resize-none`}
                maxLength={2500}
                id="note"
                {...register('note')}
                onKeyPress={combinedKeyPressHandler}
              />
            </section>
            {errors['note'] ? (
              <div className="font-semibold text-scarletRed">{errors['note']?.message?.toString()}</div>
            ) : (
              <></>
            )}
          </section>
          <section className="mt-4 flex justify-start gap-4">
            <Button className="rounded-md bg-deepBlue p-4 text-white">{Texts.adjustOrderPayment}</Button>
            <Button
              className="p-0 underline"
              onClick={() => {
                reset();
                setOpen(false);
                startRecording();
              }}
              type="reset"
            >
              {Texts.cancelButton}
            </Button>
          </section>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdjustPaymentMethodModal;
